import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Link, List, ListItem,
  ListItemText, MenuItem, Paper, Stack, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import { AuthService } from '../../../services/auth.service';
import {
  AssetRequisition, AssetRequisitionCreate, AssetRequisitionService, AssetRequisitionStatus, RequisitionAttachment, RequisitionCategory,
  RequisitionDepartment,
} from '../../../services/assetRequisition.service';
import { FixedAssetService } from '../../../services/fixedAsset.service';
import { PurchaseOrderService } from '../../../services/purchaseOrder.service';
import { FixedAsset } from '../../../interfaces/fixedAsset/fixedAsset.interface';
import { PurchaseOrder } from '../../../interfaces/procurement/purchaseOrder.interface';
import { useToast } from '../../../context/ToastContext';
import { usePermission } from '../../../hooks/usePermission';
import { PERMISSIONS } from '../../../constants/permissions';
import SearchableSelect from '../../common/SearchableSelect';
import ScrollableTable from '../../common/ScrollableTable';
import { formatDate, formatDateTime, toLocalIsoDate } from '../../../utils/dateUtils';
import { humanizeEnum } from '../../../utils/format';
import { saveBlob } from '../../../utils/saveBlob';

// ---- F8 employee asset requisition: request an asset, approval via the approvals inbox, fulfil by custodian transfer ----
export const REQUISITION_COLOR: Record<AssetRequisitionStatus, 'default' | 'warning' | 'success' | 'error' | 'info'> = {
  DRAFT: 'default', PENDING: 'warning', APPROVED: 'info', REJECTED: 'error', FULFILLED: 'success', CANCELLED: 'default',
};
const STATUSES: AssetRequisitionStatus[] = ['DRAFT', 'PENDING', 'APPROVED', 'REJECTED', 'FULFILLED', 'CANCELLED'];
const CANCELLABLE: AssetRequisitionStatus[] = ['DRAFT', 'PENDING', 'APPROVED'];

const CreateDialog: React.FC<{ open: boolean; onClose: () => void; onSaved: () => void }> = ({ open, onClose, onSaved }) => {
  const toast = useToast();
  const inst = AuthService.getSelectedInstitution()!;
  const [form, setForm] = useState<AssetRequisitionCreate>({ description: '', justification: '', departmentId: '', fixedAssetCategoryId: '', neededBy: '' });
  const [departments, setDepartments] = useState<RequisitionDepartment[]>([]);
  const [categories, setCategories] = useState<RequisitionCategory[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Both pickers are optional and come from the requisition endpoints (id + name, any member may read them).
    AssetRequisitionService.departments(inst).then(setDepartments).catch(() => setDepartments([]));
    AssetRequisitionService.categories(inst).then(setCategories).catch(() => setCategories([]));
  }, [open, inst]);

  const save = async (submit: boolean) => {
    setBusy(true);
    let created: AssetRequisition | null = null;
    try {
      created = await AssetRequisitionService.create(inst, {
        description: form.description.trim(), justification: form.justification?.trim() || undefined,
        departmentId: form.departmentId || null, fixedAssetCategoryId: form.fixedAssetCategoryId || null, neededBy: form.neededBy || null,
      });
      if (submit) await AssetRequisitionService.submit(inst, created.id);
      toast.showSuccess(submit ? `${created.requisitionNo} sent for approval` : `${created.requisitionNo} saved as draft`);
      setForm({ description: '', justification: '', departmentId: '', fixedAssetCategoryId: '', neededBy: '' });
      onSaved();
    } catch (err) {
      if (created) {
        // The draft exists; close and refresh so a retry submits it from the list instead of creating a duplicate.
        toast.showApiError(err, `${created.requisitionNo} was saved as a draft but could not be submitted`);
        setForm({ description: '', justification: '', departmentId: '', fixedAssetCategoryId: '', neededBy: '' });
        onSaved();
      } else {
        toast.showApiError(err, 'Could not save the requisition');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog data-help-page="fixedAssets.reqForm.page" open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Request an asset</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField data-help="fixedAssets.reqForm.field.description" label="What do you need?" required fullWidth value={form.description} inputProps={{ maxLength: 500 }}
            onChange={e => setForm({ ...form, description: e.target.value })} />
          <TextField data-help="fixedAssets.reqForm.field.justification" label="Why?" fullWidth multiline minRows={2} value={form.justification} inputProps={{ maxLength: 1000 }}
            onChange={e => setForm({ ...form, justification: e.target.value })} />
          <SearchableSelect<RequisitionDepartment> data-help="fixedAssets.reqForm.field.department" options={departments} value={form.departmentId ?? ''} label="Department"
            getOptionLabel={d => d.name} onChange={id => setForm({ ...form, departmentId: id })} />
          {categories.length > 0 && (
            <SearchableSelect<RequisitionCategory> data-help="fixedAssets.reqForm.field.category" options={categories} value={form.fixedAssetCategoryId ?? ''} label="Asset category"
              getOptionLabel={c => c.name} onChange={id => setForm({ ...form, fixedAssetCategoryId: id })} />
          )}
          <TextField data-help="fixedAssets.reqForm.field.neededBy" label="Needed by" type="date" fullWidth value={form.neededBy ?? ''} InputLabelProps={{ shrink: true }}
            inputProps={{ min: toLocalIsoDate(new Date()) }} onChange={e => setForm({ ...form, neededBy: e.target.value })} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button data-help="fixedAssets.reqForm.cancel" onClick={onClose}>Cancel</Button>
        <Button data-help="fixedAssets.req.action.draft" disabled={busy || !form.description.trim()} onClick={() => save(false)}>Save draft</Button>
        <Button data-help="fixedAssets.req.action.sendApproval" variant="contained" disabled={busy || !form.description.trim()} onClick={() => save(true)}>Submit for approval</Button>
      </DialogActions>
    </Dialog>
  );
};

const AttachmentsDialog: React.FC<{ req: AssetRequisition | null; onClose: () => void }> = ({ req, onClose }) => {
  const toast = useToast();
  const inst = AuthService.getSelectedInstitution()!;
  const [rows, setRows] = useState<RequisitionAttachment[]>([]);
  const input = useRef<HTMLInputElement>(null);
  const load = useCallback(async () => {
    if (!req) return;
    try { setRows(await AssetRequisitionService.attachments(inst, req.id)); } catch (err) { toast.showApiError(err, 'Could not load attachments'); }
  }, [req, inst, toast]);
  useEffect(() => { load(); }, [load]);
  const upload = async (f: File | undefined) => {
    if (!req || !f) return;
    try { await AssetRequisitionService.attach(inst, req.id, f); toast.showSuccess('Attached'); await load(); }
    catch (err) { toast.showApiError(err, 'Upload failed'); }
    finally { if (input.current) input.current.value = ''; }
  };
  const download = async (a: RequisitionAttachment) => {
    if (!req) return;
    try { saveBlob(await AssetRequisitionService.download(inst, req.id, a.id), a.fileName); }
    catch (err) { toast.showApiError(err, 'Download failed'); }
  };
  return (
    <Dialog data-help-page="fixedAssets.reqAttach.page" open={!!req} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Attachments - {req?.requisitionNo}</DialogTitle>
      <DialogContent>
        <List dense>
          {rows.length === 0 && <ListItem><ListItemText primary="No attachments yet." /></ListItem>}
          {rows.map(a => (
            <ListItem key={a.id}>
              <ListItemText primary={<Link component="button" onClick={() => download(a)}>{a.fileName}</Link>}
                secondary={`${a.uploadedByName ?? ''} ${formatDateTime(a.createdAt)}`} />
            </ListItem>
          ))}
        </List>
        <Typography variant="body2" sx={{ mt: 1 }}>Add a quote or photo (PDF, PNG or JPEG):</Typography>
        <input data-help="fixedAssets.reqAttach.field.file" ref={input} type="file" aria-label="Attachment" accept="application/pdf,image/png,image/jpeg"
          onChange={e => upload(e.target.files?.[0])} />
      </DialogContent>
      <DialogActions><Button data-help="fixedAssets.reqAttach.close" onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
};

type ActionKind = 'cancel' | 'fulfil' | 'link-po';

const ActionDialog: React.FC<{ kind: ActionKind | null; req: AssetRequisition | null; onClose: () => void; onDone: () => void }> = ({ kind, req, onClose, onDone }) => {
  const toast = useToast();
  const inst = AuthService.getSelectedInstitution()!;
  const [reason, setReason] = useState('');
  const [assetId, setAssetId] = useState('');
  const [transferDate, setTransferDate] = useState(toLocalIsoDate(new Date()));
  const [poId, setPoId] = useState('');
  const [assets, setAssets] = useState<FixedAsset[]>([]);
  const [pos, setPos] = useState<PurchaseOrder[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setReason(''); setAssetId(''); setPoId('');
    if (kind === 'fulfil') FixedAssetService.listByInstitution(inst, 0, 200).then(r => setAssets(r.content)).catch(() => setAssets([]));
    if (kind === 'link-po') PurchaseOrderService.listByInstitution(0, 200).then(r => setPos(r.content)).catch(() => setPos([]));
  }, [kind, inst]);

  const go = async () => {
    if (!req || !kind) return;
    setBusy(true);
    try {
      if (kind === 'cancel') await AssetRequisitionService.cancel(inst, req.id, reason.trim());
      if (kind === 'fulfil') await AssetRequisitionService.fulfil(inst, req.id, { assetId, transferDate, reason: reason.trim() || undefined });
      if (kind === 'link-po') await AssetRequisitionService.linkPo(inst, req.id, poId);
      toast.showSuccess('Done');
      onDone();
    } catch (err) {
      toast.showApiError(err, 'Action failed');
    } finally {
      setBusy(false);
    }
  };
  const ok = kind === 'cancel' ? !!reason.trim() : kind === 'fulfil' ? !!assetId : !!poId;
  const title = kind === 'cancel' ? 'Cancel requisition' : kind === 'fulfil' ? 'Fulfil from an existing asset' : 'Link a purchase order';

  return (
    <Dialog data-help-page="fixedAssets.reqAction.page" open={!!kind && !!req} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title} - {req?.requisitionNo}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {kind === 'fulfil' && (
            <>
              <Typography variant="body2">The asset's custodian becomes the requester (an asset transfer is recorded).</Typography>
              <SearchableSelect<FixedAsset> data-help="fixedAssets.reqAction.field.asset" options={assets} value={assetId} label="Asset"
                getOptionLabel={a => `${a.assetCode} - ${a.name}`} onChange={id => setAssetId(id)} />
              <TextField data-help="fixedAssets.reqAction.field.transferDate" label="Transfer date" type="date" fullWidth value={transferDate} InputLabelProps={{ shrink: true }}
                onChange={e => setTransferDate(e.target.value)} />
            </>
          )}
          {kind === 'link-po' && (
            <SearchableSelect<PurchaseOrder> data-help="fixedAssets.reqAction.field.purchaseOrder" options={pos} value={poId} label="Purchase order"
              getOptionLabel={p => `${p.poNumber}${p.supplierName ? ` - ${p.supplierName}` : ''}`} onChange={id => setPoId(id)} />
          )}
          {kind !== 'link-po' && (
            <TextField data-help="fixedAssets.reqAction.field.reason" label={kind === 'cancel' ? 'Reason' : 'Note (optional)'} required={kind === 'cancel'} fullWidth
              value={reason} inputProps={{ maxLength: 300 }} onChange={e => setReason(e.target.value)} />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button data-help="fixedAssets.reqAction.close" onClick={onClose}>Close</Button>
        <Button data-help="fixedAssets.req.action.confirm" variant="contained" disabled={busy || !ok} onClick={go}>Confirm</Button>
      </DialogActions>
    </Dialog>
  );
};

const AssetRequisitions: React.FC = () => {
  const toast = useToast();
  const inst = AuthService.getSelectedInstitution()!;
  const canView = usePermission(PERMISSIONS.FIXED_ASSET_VIEW);
  const canManage = usePermission(PERMISSIONS.ASSET_TRANSFER_MANAGE);
  const canAll = canView || canManage;
  const [tab, setTab] = useState<'mine' | 'all'>('mine');
  const [status, setStatus] = useState<AssetRequisitionStatus | ''>('');
  const [rows, setRows] = useState<AssetRequisition[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [attachFor, setAttachFor] = useState<AssetRequisition | null>(null);
  const [action, setAction] = useState<{ kind: ActionKind; req: AssetRequisition } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const page = tab === 'all' && canAll
        ? await AssetRequisitionService.all(inst, status || undefined)
        : await AssetRequisitionService.mine(inst);
      setRows(tab === 'mine' && status ? page.content.filter(r => r.status === status) : page.content);
    } catch (err) {
      toast.showApiError(err, 'Could not load requisitions');
    } finally {
      setLoading(false);
    }
  }, [tab, canAll, inst, status, toast]);
  useEffect(() => { load(); }, [load]);

  const submit = async (r: AssetRequisition) => {
    try { await AssetRequisitionService.submit(inst, r.id); toast.showSuccess(`${r.requisitionNo} sent for approval`); load(); }
    catch (err) { toast.showApiError(err, 'Could not submit'); }
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }} data-help-page="fixedAssets.req.page">
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1 }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>Asset requisitions</Typography>
        <Button data-help="fixedAssets.req.new" variant="contained" onClick={() => setCreateOpen(true)}>Request an asset</Button>
      </Box>
      {canAll && (
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab data-help="fixedAssets.req.tab.mine" value="mine" label="My requests" />
          <Tab data-help="fixedAssets.req.tab.all" value="all" label="All requests" />
        </Tabs>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }} data-help-section="fixedAssets.req.filter">
        <TextField data-help="fixedAssets.req.filter.status" select size="small" label="Status" value={status} sx={{ flex: '1 1 180px', maxWidth: 240 }}
          onChange={e => setStatus(e.target.value as AssetRequisitionStatus | '')}>
          <MenuItem value="">All</MenuItem>
          {STATUSES.map(s => <MenuItem key={s} value={s}>{humanizeEnum(s)}</MenuItem>)}
        </TextField>
      </Box>
      <Paper>
        {loading ? <Box sx={{ p: 3 }}><CircularProgress /></Box> : (
          <ScrollableTable>
            <Table size="small" data-help-section="fixedAssets.req.table">
              <TableHead><TableRow>
                <TableCell data-help="fixedAssets.req.col.no">No.</TableCell>
                {tab === 'all' && <TableCell data-help="fixedAssets.req.col.requester">Requester</TableCell>}
                <TableCell data-help="fixedAssets.req.col.desc">Description</TableCell><TableCell data-help="fixedAssets.req.col.dept">Department</TableCell><TableCell data-help="fixedAssets.req.col.cat">Category</TableCell>
                <TableCell data-help="fixedAssets.req.col.needed">Needed by</TableCell><TableCell data-help="fixedAssets.req.col.status">Status</TableCell><TableCell data-help="fixedAssets.req.col.outcome">Outcome</TableCell><TableCell data-help="fixedAssets.req.col.actions" />
              </TableRow></TableHead>
              <TableBody>
                {rows.length === 0 && <TableRow><TableCell colSpan={9} align="center">No requisitions.</TableCell></TableRow>}
                {rows.map(r => (
                  <TableRow key={r.id}>
                    <TableCell>{r.requisitionNo}</TableCell>
                    {tab === 'all' && <TableCell>{r.requesterName ?? ''}</TableCell>}
                    <TableCell>{r.description}</TableCell>
                    <TableCell>{r.departmentName ?? ''}</TableCell>
                    <TableCell>{r.categoryName ?? ''}</TableCell>
                    <TableCell>{r.neededBy ? formatDate(r.neededBy) : ''}</TableCell>
                    <TableCell><Chip size="small" label={humanizeEnum(r.status)} color={REQUISITION_COLOR[r.status]} /></TableCell>
                    <TableCell>
                      {r.fulfilledAssetCode ? `Asset ${r.fulfilledAssetCode}` : ''}
                      {r.purchaseOrderNo ? ` PO ${r.purchaseOrderNo}` : ''}
                      {r.cancelReason ? `Cancelled: ${r.cancelReason}` : ''}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {tab === 'mine' && r.status === 'DRAFT' && <Button data-help="fixedAssets.req.action.submit" size="small" onClick={() => submit(r)}>Submit</Button>}
                      {tab === 'mine' && CANCELLABLE.includes(r.status) && (
                        <Button data-help="fixedAssets.req.action.cancel" size="small" color="warning" onClick={() => setAction({ kind: 'cancel', req: r })}>Cancel</Button>
                      )}
                      {canManage && r.status === 'APPROVED' && (
                        <>
                          <Button data-help="fixedAssets.req.action.fulfil" size="small" onClick={() => setAction({ kind: 'fulfil', req: r })}>Fulfil</Button>
                          {!r.purchaseOrderId && <Button data-help="fixedAssets.req.action.linkPo" size="small" onClick={() => setAction({ kind: 'link-po', req: r })}>Link PO</Button>}
                        </>
                      )}
                      <Button data-help="fixedAssets.req.action.files" size="small" onClick={() => setAttachFor(r)}>Files</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollableTable>
        )}
      </Paper>
      <CreateDialog open={createOpen} onClose={() => setCreateOpen(false)} onSaved={() => { setCreateOpen(false); load(); }} />
      <AttachmentsDialog req={attachFor} onClose={() => setAttachFor(null)} />
      <ActionDialog kind={action?.kind ?? null} req={action?.req ?? null} onClose={() => setAction(null)}
        onDone={() => { setAction(null); load(); }} />
    </Box>
  );
};

export default AssetRequisitions;
// ---- end F8 ----
